import React, { useEffect, useRef, useState } from 'react';
import { prefersLiteEffects } from '../../utils/perf';
import './SiteCanvasBackground.css';

const IFRAME_SRC = '/blackhole-webgpu.html?v=20260731-smooth1';

/**
 * Intro cinematic: 1 = Shot A (hero), 0 = Shot C (final About framing).
 * Starts during late hero; final pose lands only after About has scrolled well in
 * so the two-shot path has room before settling.
 */
const INTRO_TOP_RANGE_START = 1.65;
const INTRO_TOP_RANGE_END = -0.62;

function computeIntroScrollProgress() {
  const aboutEl = document.getElementById('about');
  // Default to the distant/hero frame so a missing anchor never flashes the
  // full-dolly center pose while the page is still mounting under the loader.
  if (!aboutEl) return 1;
  const top = aboutEl.getBoundingClientRect().top;
  const vh = window.innerHeight;
  const rangeStart = vh * INTRO_TOP_RANGE_START;
  const rangeEnd = vh * INTRO_TOP_RANGE_END;
  const raw = (rangeStart - top) / Math.max(1, rangeStart - rangeEnd);
  const clamped = Math.min(1, Math.max(0, raw));
  return 1 - clamped;
}

/**
 * Single full-viewport WebGPU layer. Stars + nebula + black hole come from the iframe shader.
 * Framing: hero intro dolly + idle/pointer orbit inside the iframe (no About pin path).
 * On mobile / low-power devices we render a static CSS fallback instead.
 */
const SiteCanvasBackground = () => {
  const iframeRef = useRef(null);
  const rafRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const pausedRef = useRef(false);
  const [useLite] = useState(() => prefersLiteEffects());

  useEffect(() => {
    if (useLite) return undefined;

    const onPointerMove = (event) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      pointerRef.current = {
        x: (event.clientX / w) * 2 - 1,
        y: (event.clientY / h) * 2 - 1
      };
    };
    window.addEventListener('mousemove', onPointerMove, { passive: true });
    return () => window.removeEventListener('mousemove', onPointerMove);
  }, [useLite]);

  useEffect(() => {
    if (useLite) {
      // Static fallback — tell the loader it can finish without waiting on WebGPU.
      try {
        window.postMessage({ type: 'blackhole-ready' }, window.location.origin);
      } catch {
        // ignore
      }
      return undefined;
    }

    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const postToIframe = (payload) => {
      const win = iframe.contentWindow;
      if (!win) return;
      try {
        win.postMessage(payload, window.location.origin);
      } catch {
        // ignore
      }
    };

    const postFrame = () => {
      if (pausedRef.current) return;
      postToIframe({
        type: 'intro-scroll',
        progress: computeIntroScrollProgress()
      });
      postToIframe({
        type: 'pointer',
        x: pointerRef.current.x,
        y: pointerRef.current.y
      });
    };

    const loop = () => {
      postFrame();
      rafRef.current = requestAnimationFrame(loop);
    };

    const startLoop = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (!pausedRef.current) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    const stopLoop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const setPaused = (paused) => {
      pausedRef.current = paused;
      postToIframe({ type: paused ? 'pause' : 'resume' });
      if (paused) stopLoop();
      else startLoop();
    };

    const onVisibility = () => {
      setPaused(document.hidden);
    };

    const onIframeLoad = () => {
      setPaused(document.hidden);
      if (!document.hidden) startLoop();
    };

    iframe.addEventListener('load', onIframeLoad);
    document.addEventListener('visibilitychange', onVisibility);
    if (iframe.contentWindow?.document?.readyState === 'complete') {
      onIframeLoad();
    }

    return () => {
      iframe.removeEventListener('load', onIframeLoad);
      document.removeEventListener('visibilitychange', onVisibility);
      stopLoop();
    };
  }, [useLite]);

  if (useLite) {
    return (
      <div
        className="siteCanvasBackground siteCanvasBackground--static"
        aria-hidden="true"
      />
    );
  }

  return (
    <div className="siteCanvasBackground" aria-hidden="true">
      <iframe
        ref={iframeRef}
        className="siteCanvasBackground__iframe"
        title="site-background-webgpu"
        src={IFRAME_SRC}
        loading="eager"
      />
    </div>
  );
};

export default SiteCanvasBackground;
